import { useState, useEffect, useMemo } from "react";
import { Modal, Typography, Button, Tag, TimePicker, Alert, Space, Checkbox, AutoComplete, Select, Tooltip } from "antd";
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useSelector } from "react-redux";
import { getUnitAcronym, cleanName, getActiveEarliestUnit } from "../../../../utils/ouberUtils";

dayjs.extend(customParseFormat);
const { Text, Title } = Typography;

const UserViewTripModal = ({
  isOpen,
  onClose,
  tripData,
  onCancelTrip,
  onRequestJoin,
  onUpdateMyPickup,
  hasActivePosition = true,
  noPositionMessage = "",
}) => {
  const [allowCustomPickup, setAllowCustomPickup] = useState(false);
  const [customPickupVenue, setCustomPickupVenue] = useState("");
  const [customPickupTime, setCustomPickupTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditingOwnRequest, setIsEditingOwnRequest] = useState(false);

  const currentUser = useSelector((state) => state.accountDetailSlice?.details);
  const myEmployeeId = Number(currentUser?.id || currentUser?.employee_id);

  const userUnits = useMemo(() => {
    if (!currentUser?.positions) return [];
    
    const todayStr = dayjs().format("YYYY-MM-DD");
    // Get unique unit acronyms for active positions
    let units = currentUser.positions
      .filter(p => {
        const endDate = p.pivot?.position_end_date || p.position_end_date;
        if (!endDate) return true;
        return endDate >= todayStr;
      })
      .map(p => getUnitAcronym(p.unit || p.department))
      .filter(u => u && u !== 'N/A');

    if (units.length === 0 && currentUser.department) {
      const dept = getUnitAcronym(currentUser.department);
      if (dept && dept !== 'N/A') {
        units = [dept];
      }
    }

    return [...new Set(units)];
  }, [currentUser]);

  const showUnitDropdown = userUnits.length > 1;
  const [selectedUnit, setSelectedUnit] = useState("");

  useEffect(() => {
    if (userUnits.length === 1) {
      setSelectedUnit(userUnits[0]);
    } else if (userUnits.length > 1) {
      setSelectedUnit("");
    }
  }, [userUnits, isOpen]);

  const userPassengerRecord = tripData?.passengers?.find(p => 
    myEmployeeId && (Number(p.id) === myEmployeeId || Number(p.employee_id) === myEmployeeId)
  );

  // Populates the pickup fields from the passenger's existing request —
  // used both on modal open and to discard an in-progress edit.
  const syncPickupFromExisting = () => {
    if (userPassengerRecord && tripData) {
      const requestsArr = Array.isArray(tripData.requests)
        ? tripData.requests
        : (typeof tripData.requests === 'object' && tripData.requests !== null
            ? Object.values(tripData.requests)
            : []);

      const userRequest = requestsArr.find(r => {
        const rId = Number(r.employee_id || r.id || r.user_id);
        if (rId && rId === myEmployeeId) return true;
        const rName = (r.name || r.external_passenger_name || r.display_name || "").toLowerCase().trim();
        const myName = (currentUser.name || (currentUser.first_name + " " + currentUser.last_name) || "").toLowerCase().trim();
        return rName && myName && (rName.includes(myName) || myName.includes(rName));
      });

      const pVenue = userPassengerRecord.pickup_venue || userPassengerRecord.pivot?.pickup_venue || userRequest?.pickup_venue;
      const pTime = userPassengerRecord.estimated_pickup_time || userPassengerRecord.pivot?.estimated_pickup_time || userRequest?.estimated_pickup_time;

      const tVenue = tripData.pickup_venue || tripData.pickup;
      const tTime = tripData.pickup_time || tripData.time;

      // Robust Parsing helper - handles AM/PM and HH:mm:ss
      const formatDisplayTime = (t) => {
        if (!t) return null;
        const fmts = ["HH:mm:ss", "h:mm a", "h:mm A", "HH:mm", "hh:mm a", "hh:mm A", "HH:mm:ss.SSSSSS"];
        const d = dayjs(t, fmts);
        return d.isValid() ? d.format("h:mm a") : t;
      };

      const pTimeFmt = formatDisplayTime(pTime);
      const tTimeFmt = formatDisplayTime(tTime);

      const isDiffVenue = pVenue && pVenue !== tVenue;
      const isDiffTime = pTimeFmt && pTimeFmt !== tTimeFmt;

      if (isDiffVenue || isDiffTime) {
        setAllowCustomPickup(true);
        setCustomPickupVenue(pVenue || "");
        const fmts = ["HH:mm:ss", "h:mm a", "h:mm A", "HH:mm", "hh:mm a", "hh:mm A", "HH:mm:ss.SSSSSS"];
        setCustomPickupTime(pTime ? dayjs(pTime, fmts) : null);
      } else {
        setAllowCustomPickup(false);
        setCustomPickupVenue("");
        setCustomPickupTime(null);
      }
    } else {
      setAllowCustomPickup(false);
      setCustomPickupVenue("");
      setCustomPickupTime(null);
    }
  };

  // Sync local state when modal opens or tripData changes
  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setIsEditingOwnRequest(false);
      syncPickupFromExisting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userPassengerRecord, tripData, currentUser, myEmployeeId]);

  // SAFE GUARD: Early exit after hooks
  if (!tripData) return null;

  const headerBgColor = "#862633"; 

  const censorPlate = (plate) => {
    if (!plate || plate === "N/A") return "N/A";
    const prefix = plate.substring(0, 2);
    const masked = "*".repeat(Math.max(0, plate.length - 2));
    return prefix + masked;
  };

  const parseCapacity = (capStr) => {
    if (!capStr) return { current: 0, total: 0, isFull: false };
    const parts = capStr.split("/");
    const current = Number(parts[0]);
    const total = Number(parts[1]);
    return { isFull: current >= total };
  };

  const { isFull } = parseCapacity(tripData.capacity);
  const currentStatus = tripData.status || tripData.trip_status;
  
  const now = dayjs();
  const tripTimeStr = tripData.pickup_time || tripData.time;
  const tripStartStr = `${tripData.date} ${tripTimeStr}`;
  const tripDayjs = dayjs(tripStartStr, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD h:mm a", "YYYY-MM-DD HH:mm", "YYYY-MM-DD"]);
  const isPast = tripDayjs.isValid() ? now.isAfter(tripDayjs) : false;

  // Deadline logic
  const tBaseDate = tripData.trip_date || tripData.date;
  const dDate = tripData.deadline_date || (tBaseDate ? dayjs(tBaseDate).subtract(1, 'day').format('YYYY-MM-DD') : null);
  const dTime = tripData.deadline_time || "17:00:00";
  const deadlineDayjs = dDate ? dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]) : dayjs().add(100, 'year');
  const isDeadlinePassed = now.isAfter(deadlineDayjs);

  const isTripLocked = currentStatus === "In Progress" || currentStatus === "Completed Trip" || currentStatus === "Cancelled by AO" || currentStatus === "Cancelled by User" || isPast;
  const isUnavailable = currentStatus === "Unavailable" || currentStatus === "Full Capacity" || isFull || isTripLocked || isDeadlinePassed;
  
  const isAlreadyPassenger = !!userPassengerRecord;

  const getStatusIcon = (status) => {
    switch(status) {
      case "Confirmed": return <CheckCircleOutlined style={{ color: "#33A780" }}/>;
      case "Approved by AO": return <CheckCircleOutlined style={{ color: "#33A780" }}/>; 
      case "Available": return <CheckCircleOutlined style={{ color: "#33A780" }}/>; 
      case "Completed Trip": return <CheckCircleOutlined style={{ color: "#33A780" }}/>; 
      case "Unavailable": return <CloseCircleOutlined style={{ color: "#A39F9F" }} />;
      case "Cancelled by AO":
      case "Cancelled by User": return <CloseCircleOutlined style={{ color: "#A39F9F" }} />;
      default: return <ClockCircleOutlined style={{ color: "#E4A54B" }} />;
    }
  };

  // No predefined venues — the employee types their own custom pickup venue.
  const venueOptions = [];

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  // Only send custom logistics if enabled AND valid; otherwise fall back
  // to the trip's default pickup venue/time. Returns null when a custom
  // pickup was requested but left blank (caller should abort).
  const buildLogisticsPayload = () => {
    if (allowCustomPickup) {
      const sanitizedVenue = sanitizeInput(customPickupVenue);
      if (!sanitizedVenue) return null;
      const payload = { pickup_venue: sanitizedVenue };
      if (customPickupTime) payload.estimated_pickup_time = customPickupTime.format('HH:mm:ss');
      return payload;
    }

    const payload = { pickup_venue: tripData.pickup_venue || tripData.pickup };
    const tripTimeRaw = tripData.pickup_time || tripData.time;
    if (tripTimeRaw) {
      const parsed = dayjs(tripTimeRaw, ["h:mm a", "HH:mm:ss", "HH:mm"]);
      if (parsed.isValid()) payload.estimated_pickup_time = parsed.format("HH:mm:ss");
    }
    return payload;
  };

  const handleAction = async () => {
    if (!onRequestJoin) return;

    const logistics = buildLogisticsPayload();
    if (!logistics) return;

    const payload = {
      trip_id: tripData.id,
      trip_date: tripData.date,
      dropoff_location: tripData.destination,
      trip_purpose: tripData.purpose,
      unit: selectedUnit,
      ...logistics,
    };

    setLoading(true);
    try {
      await onRequestJoin(payload);
    } catch (e) {
      // Handled by parent
    } finally {
      setLoading(false);
    }
  };

  // Lets a passenger who's already on this trip change their own pickup
  // venue/time (and unit). If the request was already approved, the
  // backend bumps it back to pending and notifies the organizing AO.
  const handleSaveOwnPickup = async () => {
    if (!onUpdateMyPickup) return;

    const logistics = buildLogisticsPayload();
    if (!logistics) return;

    const requestId = userPassengerRecord?.pivot?.trip_request_id || userPassengerRecord?.trip_request_id;
    if (!requestId) return;

    setLoading(true);
    try {
      await onUpdateMyPickup(requestId, { ...logistics, unit: selectedUnit });
      setIsEditingOwnRequest(false);
    } catch (e) {
      // Handled by parent
    } finally {
      setLoading(false);
    }
  };

  const isMultipleAllowed = !!(
    tripData.isMultiplePickupAllowed || 
    tripData.allowed_multiple_pickup === 1 || 
    tripData.allowed_multiple_pickup === true ||
    tripData.allowed_multiple_pickup === "1" ||
    tripData.allowed_multiple_pickups === 1 ||
    tripData.allowed_multiple_pickups === true ||
    tripData.allow_multiple_pickup === 1 ||
    tripData.allow_multiple_pickups === 1
  );

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      zIndex={1050}
      closeIcon={<CloseOutlined style={{ color: "white" }} />}
      styles={{
        header: { backgroundColor: headerBgColor, padding: "16px 24px", borderRadius: "8px 8px 0 0", margin: "-20px -24px 12px -24px" },
        title: { color: "white", margin: 0 },
        body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', margin: '0 -24px', padding: '12px 24px' }
      }}
      title={<Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>Trip Details</Title>}
      className="ouber-modal white-close-icon"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '8px' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Deadline of Requests</Text>
            <Text strong style={{ color: isDeadlinePassed ? "#cf1322" : "inherit" }}>
              {deadlineDayjs && deadlineDayjs.isValid() ? deadlineDayjs.format('MMMM D, YYYY h:mm A') : "N/A"}
              {isDeadlinePassed && " (Passed)"}
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Status of Trip</Text>
            <Space>
              <Text strong>
                {(() => {
                  const userStatus = userPassengerRecord?.status || userPassengerRecord?.pivot?.request_status;
                  return userStatus === "Requested to Join" ? "Requested to Join" : currentStatus;
                })()}
              </Text>
              {(() => {
                const userStatus = userPassengerRecord?.status || userPassengerRecord?.pivot?.request_status;
                return getStatusIcon(userStatus === "Requested to Join" ? "Requested to Join" : currentStatus);
              })()}
            </Space>
          </div>
        </div>

        <div style={{ border: '1px solid #f0f0f0', padding: '16px', borderRadius: '8px' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>Starting Pickup Point</Text>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text strong style={{ fontSize: '16px' }}>
                {isAlreadyPassenger 
                  ? (userPassengerRecord.pickup_venue || userPassengerRecord.pivot?.pickup_venue || tripData.pickup_venue || tripData.pickup)
                  : (tripData.pickup_venue || tripData.pickup)}
              </Text>
              {isAlreadyPassenger && (() => {
                const requestsArr = Array.isArray(tripData.requests) 
                  ? tripData.requests 
                  : (typeof tripData.requests === 'object' && tripData.requests !== null 
                      ? Object.values(tripData.requests) 
                      : []);
                const req = requestsArr.find(r => {
                  const rId = Number(r.employee_id || r.id || r.user_id);
                  if (rId && rId === myEmployeeId) return true;
                  const rName = (r.name || r.external_passenger_name || r.display_name || "").toLowerCase().trim();
                  const myName = (currentUser.name || (currentUser.first_name + " " + currentUser.last_name) || "").toLowerCase().trim();
                  return rName && myName && (rName.includes(myName) || myName.includes(rName));
                });
                const pTimeRaw = userPassengerRecord.estimated_pickup_time || userPassengerRecord.pivot?.estimated_pickup_time || req?.estimated_pickup_time;
                const pVenueRaw = userPassengerRecord.pickup_venue || userPassengerRecord.pivot?.pickup_venue || req?.pickup_venue;
                
                const tTime = tripData.pickup_time || tripData.time;
                const tVenue = tripData.pickup_venue || tripData.pickup;
                
                const formatTime = (t) => {
                  if (!t) return null;
                  const fmts = ["HH:mm:ss", "h:mm a", "H:mm:ss", "HH:mm"];
                  return dayjs(t, fmts).isValid() ? dayjs(t, fmts).format("h:mm a") : t;
                };

                const pTimeFmt = formatTime(pTimeRaw);
                const tTimeFmt = formatTime(tTime);
                
                const isDiffVenue = pVenueRaw && pVenueRaw.toString().trim() !== tVenue.toString().trim();
                const isDiffTime = pTimeFmt && pTimeFmt !== tTimeFmt;

                if (!isDiffVenue && !isDiffTime) return null;

                return (
                  <Text type="secondary" style={{ fontSize: '13px', color: '#000000', fontWeight: 500 }}>
                    <ClockCircleOutlined style={{ marginRight: '4px' }} />
                    {pTimeFmt || tTimeFmt}
                  </Text>
                );
              })()}
            </div>
          </div>

          {isMultipleAllowed ? (
            <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
              <Checkbox 
                checked={allowCustomPickup} 
                disabled={isTripLocked || (isAlreadyPassenger && !isEditingOwnRequest) || isDeadlinePassed} 
                onChange={(e) => setAllowCustomPickup(e.target.checked)}
                style={{ fontSize: '13px' }}
              >
                I would like to be picked up in a different location
              </Checkbox>
              {allowCustomPickup && (
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Pickup Venue</Text>
                    <AutoComplete 
                      style={{ width: '100%' }} 
                      options={venueOptions} 
                      placeholder="E.g., UPLB Gate" 
                      disabled={isTripLocked || (isAlreadyPassenger && !isEditingOwnRequest) || isDeadlinePassed} 
                      value={customPickupVenue} 
                      onChange={setCustomPickupVenue}
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Est. Pickup Time</Text>
                    <TimePicker 
                      format="h:mm a" 
                      use12Hours
                      style={{ width: '100%' }} 
                      disabled={isTripLocked || (isAlreadyPassenger && !isEditingOwnRequest) || isDeadlinePassed} 
                      value={customPickupTime} 
                      onChange={setCustomPickupTime}
                      disabledTime={() => {
                        const tripStart = dayjs(tripData.pickup_time || tripData.time, ["HH:mm:ss", "h:mm a", "H:mm:ss"]);
                        const tripEnd = dayjs(tripData.estimated_arrival_time || tripData.estimatedArrival, ["HH:mm:ss", "h:mm a", "H:mm:ss"]);
                        
                        if (!tripStart.isValid() || !tripEnd.isValid()) return {};

                        return {
                          disabledHours: () => {
                            const hours = [];
                            for (let i = 0; i < 24; i++) {
                              if (i < tripStart.hour() || i > tripEnd.hour()) hours.push(i);
                            }
                            return hours;
                          },
                          disabledMinutes: (h) => {
                            const minutes = [];
                            if (h === tripStart.hour()) {
                              for (let i = 0; i < tripStart.minute(); i++) minutes.push(i);
                            }
                            if (h === tripEnd.hour()) {
                              for (let i = tripEnd.minute() + 1; i < 60; i++) minutes.push(i);
                            }
                            return minutes;
                          }
                        };
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: '12px' }}>
              <Alert 
                message="Reminder: Pickups are only at the designated starting point." 
                type="warning" 
                showIcon 
                style={{ fontSize: '11px', padding: '4px 8px' }}
              />
            </div>
          )}
        </div>

        {userUnits.length > 1 && (
          <div style={{ border: '1px solid #f0f0f0', padding: '16px', borderRadius: '8px' }}>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Unit to Represent <Text type="danger">*</Text></Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select which unit you represent..."
              value={selectedUnit || undefined}
              onChange={setSelectedUnit}
              disabled={isTripLocked || (isAlreadyPassenger && !isEditingOwnRequest) || isDeadlinePassed}
            >
              {userUnits.map(unit => (
                <Select.Option key={unit} value={unit}>{unit}</Select.Option>
              ))}
            </Select>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Trip Route</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text>{tripData.pickup}</Text> <ArrowRightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} /> <Text>{tripData.destination}</Text>
            </div>
          </div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Date</Text><Text>{dayjs(tripData.date).format("MMMM D, YYYY")}</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Time</Text><Text>{tripData.time}</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Estimated Arrival</Text><Text>{tripData.estimatedArrival || "TBD"}</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Driver</Text><Text>{typeof tripData.driver === 'string' ? tripData.driver : (tripData.driver?.driver_name || tripData.custom_driver_name || "Unassigned")}</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Contact</Text><Text>{tripData.contactNumber || tripData.driverContact || tripData.driver?.contact_number || tripData.custom_driver_contact || "N/A"}</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Vehicle</Text><Text>{typeof tripData.vehicle === 'string' ? tripData.vehicle : (tripData.vehicle?.vehicle_model || tripData.custom_vehicle_name || "Unassigned")} ({censorPlate(tripData.plateNumber || tripData.vehicle?.plate_number || tripData.custom_vehicle_plate || "N/A")})</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Capacity</Text><Text style={{ color: isFull ? "#cf1322" : "inherit", fontWeight: isFull ? 600 : 400 }}>{tripData.capacity || "N/A"} {isFull && "(Full)"}</Text></div>
          <div><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Organizing Unit</Text><Text>{getUnitAcronym(tripData.organizing_unit)}</Text></div>
          <div style={{ gridColumn: 'span 2' }}><Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Purpose of Trip</Text><Text>{tripData.purpose || "Official Business"}</Text></div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Passengers</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>{tripData.passengers?.length || 0} Registered</Text>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '8px' }}>
            {tripData.passengers && tripData.passengers.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px 16px' }}>
                {tripData.passengers
                  .sort((a, b) => {
                    const requestsArr = Array.isArray(tripData.requests) ? tripData.requests : (typeof tripData.requests === 'object' && tripData.requests !== null ? Object.values(tripData.requests) : []);
                    const getT = (p) => {
                      const req = requestsArr.find(r => Number(r.employee_id || r.id || r.user_id) === Number(p.id || p.employee_id));
                      const raw = p.estimated_pickup_time || p.pivot?.estimated_pickup_time || req?.estimated_pickup_time || tripData.pickup_time || tripData.time;
                      return dayjs(raw, ["HH:mm:ss", "h:mm a", "H:mm:ss"]);
                    };
                    return getT(a).diff(getT(b));
                  })
                  .map((p) => {
                  const isRequestor = !!(p.is_requestor || p.pivot?.is_requestor);
                  const isPending = p.pivot?.request_status === "Pending" || p.pivot?.request_status === "Requested to Join" || p.status === "Pending" || p.status === "Requested to Join";
                  const isUserRef = myEmployeeId && (p.id === myEmployeeId || p.employee_id === myEmployeeId);
                  return (
                    <div key={p.id || Math.random()} style={{ padding: '4px 0', borderBottom: '1px solid #f9f9f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minHeight: '28px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, flexWrap: 'wrap', gap: '4px' }}>
                          <Text strong style={{ fontSize: '13px' }}>{isUserRef ? "You" : `${cleanName(p.first_name || p.name || "Unknown")} ${cleanName(p.last_name || "")}`}</Text>
                          {!p.is_external && (
                            <Text type="secondary" style={{ fontWeight: 400, fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                              ({getUnitAcronym(getActiveEarliestUnit(p))})
                            </Text>
                          )}
                          {p.is_external && <Tag color="orange" style={{ border: 'none', margin: 0, fontSize: '9px', lineHeight: '16px' }}>Guest</Tag>}
                          {isRequestor ? <Tag color="gold" style={{ border: 'none', margin: 0, fontSize: '10px' }}>Head of the Party</Tag> : isPending ? <Tag color="warning" style={{ border: 'none', margin: 0, fontSize: '10px' }}>Requested to Join</Tag> : <Tag color="success" style={{ border: 'none', margin: 0, fontSize: '10px' }}>Approved by AO</Tag>}
                        </div>
                        </div>
                      </div>
                  );
                })}
              </div>
            ) : ( <div style={{ padding: '8px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: '12px' }}>No passengers joined yet.</Text></div> )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
          <Button onClick={onClose} disabled={loading}>Close</Button>
          {!isTripLocked && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {isAlreadyPassenger ? (
                isEditingOwnRequest ? (
                  <>
                    <Button
                      onClick={() => {
                        syncPickupFromExisting();
                        setIsEditingOwnRequest(false);
                      }}
                      disabled={loading}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      type="primary"
                      className="ouber-btn-primary"
                      disabled={(allowCustomPickup && !customPickupVenue?.trim()) || (showUnitDropdown && !selectedUnit)}
                      onClick={handleSaveOwnPickup}
                      loading={loading}
                    >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <>
                    {!isDeadlinePassed && (isMultipleAllowed || showUnitDropdown) && (
                      <Button onClick={() => setIsEditingOwnRequest(true)} disabled={loading}>Edit Pickup</Button>
                    )}
                    <Button className="ouber-btn-danger" onClick={() => onCancelTrip(userPassengerRecord?.pivot?.trip_request_id || userPassengerRecord?.trip_request_id)} disabled={loading}>Cancel My Request</Button>
                  </>
                )
              ) : (
                <Tooltip title={!hasActivePosition ? noPositionMessage : ""}>
                  <Button
                    type="primary"
                    className="ouber-btn-primary"
                    disabled={!hasActivePosition || isUnavailable || (allowCustomPickup && !customPickupVenue?.trim()) || (showUnitDropdown && !selectedUnit)}
                    onClick={handleAction}
                    loading={loading}
                  >
                    Request to Join
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserViewTripModal;
